import React from 'react';
import { Link } from 'react-router-dom';

const Disclaimer = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Disclaimer
                </h1>
                <p className="text-gray-600">
                    Important information about using GradPath
                </p>
                <p className="text-sm text-gray-500 mt-2">
                    Last updated: June 1, 2026
                </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p className="text-yellow-800 text-sm">
                        <i className="fas fa-info-circle mr-2"></i>
                        Please read this disclaimer carefully before using GradPath.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-info-circle text-gradpath-green text-lg"></i>
                        General Information
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        The information provided on GradPath is for general informational purposes only. 
                        While we strive to keep information accurate and up-to-date, we make no representations or 
                        warranties of any kind, express or implied, about the completeness, accuracy, reliability, 
                        suitability, or availability of any information on the Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-external-link-alt text-gradpath-green text-lg"></i>
                        External Links
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        GradPath contains links to external websites and job postings. We have no control over the 
                        content, privacy policies, or practices of these sites and assume no responsibility for them. 
                        The inclusion of any link does not imply endorsement or recommendation by GradPath.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-briefcase text-gradpath-green text-lg"></i>
                        Job Listings
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Job postings are collected from external sources or posted by third-party employers. 
                        We do not guarantee the availability, accuracy, legitimacy, or currency of any job posting. 
                        Users are strongly encouraged to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Verify job details directly with the employer</li>
                        <li>Be cautious of potential job scams</li>
                        <li>Never share sensitive personal information unnecessarily</li>
                        <li>Report suspicious job postings to us</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-chart-line text-gradpath-green text-lg"></i>
                        No Guarantee of Employment
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Using GradPath does not guarantee job placement, interviews, or employment. 
                        We are a job aggregation and career guidance platform, not a recruiter or employer. 
                        Your success in finding employment depends on many factors including your qualifications, 
                        interview performance, and market conditions.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-gavel text-gradpath-green text-lg"></i>
                        Professional Advice Disclaimer
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        The career advice, blog articles, and CV/cover letter templates provided on GradPath are 
                        for general guidance only and should not be considered professional or legal advice. 
                        For specific career or legal concerns, you should consult with qualified professionals.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-laptop-code text-gradpath-green text-lg"></i>
                        Technical Disclaimer
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        While we strive to maintain 99.9% uptime, we do not guarantee that the Platform will be 
                        uninterrupted, error-free, or free from viruses or other harmful components. We are not liable 
                        for any technical issues, data loss, or interruptions in service.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-user-shield text-gradpath-green text-lg"></i>
                        Limitation of Liability
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        To the fullest extent permitted by South African law, GradPath and its owners, employees, 
                        and affiliates shall not be liable for any direct, indirect, incidental, consequential, 
                        or punitive damages arising from your use of or inability to use the Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-sync-alt text-gradpath-green text-lg"></i>
                        Changes to This Disclaimer
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We reserve the right to update this disclaimer at any time. Changes become effective immediately 
                        upon posting. Your continued use of GradPath constitutes acceptance of the updated disclaimer.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Report Issues
                    </h2>
                    <p className="text-gray-700 text-sm">
                        If you encounter any suspicious job postings or technical issues, please report them to:
                        <a href="mailto:support@gradpath.co.za" className="text-gradpath-green hover:underline block mt-1">
                            support@gradpath.co.za
                        </a>
                    </p>
                </section>

                {/* Back to Home Link */}
                <div className="text-center pt-4">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-gradpath-green hover:underline"
                    >
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Disclaimer;